import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/auth/AuthContext';
import WebCallModal from '../../components/WebCallModal';
import AgentDetails from './AgentDetails';
import CreateAgentForm from '../../components/CreateAgentForm';
import { API_BASE_URL } from '../../config';

const Agents = ({ onCategoryClick, paidCategories, addPaidCategory, onOpenAssistant }) => {
  // ============================================================================
  // AUTH & USER DATA
  // ============================================================================
  // Get authentication state and user email from auth context
  const { isUserLoggedIn, userEmail } = useAuth();
  
  // ✅ FIX: Get userId from localStorage instead of calling /api/user-info
  // This eliminates the 404 error and "Unexpected token '<'" JSON parse error
  const storedUser = JSON.parse(localStorage.getItem('chatbotUser') || 'null');
  const userId = storedUser?.userId || storedUser?.id || null;
  
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [detailsAgent, setDetailsAgent] = useState(null);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  // Helper to normalize category names for comparison
  const normalize = str => (str || '').toLowerCase().trim();
  
  // Check if user is admin
  const isAdmin = () => {
    // Try to get email from chatbotUser object first (new format)
    const chatbotUser = localStorage.getItem('chatbotUser');
    let adminEmail = '';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        adminEmail = user.email || '';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    // Fallback to old format (direct userEmail key)
    if (!adminEmail) {
      adminEmail = localStorage.getItem('userEmail') || '';
    }
    
    // Also check from useAuth if available
    if (!adminEmail && userEmail) {
      adminEmail = userEmail;
    }
    
    return adminEmail.toLowerCase() === 'info@urcommunity.com.au';
  };

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  
  // ❌ REMOVED: The problematic /api/user-info fetch that was causing 404 errors
  // Previously this code was here:
  /*
  useEffect(() => {
    if (isUserLoggedIn && userEmail) {
      fetch(`${API_BASE_URL}/api/user-info?email=${encodeURIComponent(userEmail)}`)
        .then(response => response.json())
        .then(data => {
          if (data.success && data.user) {
            setUserId(data.user.id);
          }
        })
        .catch(error => {
          console.error('Error fetching user ID:', error);
        });
    }
  }, [isUserLoggedIn, userEmail]);
  */
  // ✅ Now we get userId directly from localStorage above, eliminating this unnecessary API call

  // Fetch user's purchased agents with full details from Retell
  useEffect(() => {
    // Only fetch if user is logged in and we have their email
    if (isUserLoggedIn && userEmail) {
      setLoading(true);
      setError(null);
      
      // Fetch all agents associated with this user's email
      fetch(`${API_BASE_URL}/api/user-agents?user_email=${encodeURIComponent(userEmail)}`)
        .then(res => {
          // ✅ IMPROVEMENT: Check if response is ok before parsing JSON
          if (!res.ok) {
            throw new Error(`Failed to fetch agents: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          // Validate that we received an array of agents
          if (Array.isArray(data)) {
            setAgents(data);
            
            // Update paid categories if the function is provided
            // This keeps the category list in sync with purchased agents
            if (addPaidCategory) {
              data.forEach(agent => {
                addPaidCategory(agent.agent_name);
              });
            }
          } else {
            setError('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching user agents:', err);
          setError('Failed to fetch agents');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // User not logged in, stop loading
      setLoading(false);
    }
  }, [isUserLoggedIn, userEmail, addPaidCategory]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  // Refresh agents list manually
  const handleRefreshAgents = async () => {
    if (!isUserLoggedIn || !userEmail) {
      alert('Please log in to refresh agents');
      return;
    }

    setIsRefreshing(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/user-agents?user_email=${encodeURIComponent(userEmail)}`);
      
      if (!res.ok) {
        throw new Error(`Failed to refresh agents: ${res.status}`);
      }

      const data = await res.json();
      
      if (Array.isArray(data)) {
        setAgents(data);
        
        // Update paid categories
        if (addPaidCategory) {
          data.forEach(agent => {
            addPaidCategory(agent.agent_name);
          });
        }
      } else {
        setError('Invalid response format');
      }
    } catch (err) {
      console.error('❌ Error refreshing agents:', err);
      setError('Failed to refresh agents');
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Handle category selection (if using category filtering)
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    if (onCategoryClick) onCategoryClick(category);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    // Search logic is handled by filteredAgents below
    // This just prevents form submission from reloading the page
  };

  // Handle search input changes
  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle creating a web call for an agent
  const handleCreateWebCall = (agent) => {
    // Ensure user is logged in before allowing web call creation
    if (!isUserLoggedIn || !userEmail) {
      alert('Please log in to create a web call');
      return;
    }

    setSelectedAgent(agent);
    setIsModalOpen(true);
  };

  // Close the web call modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  // ============================================================================
  // CREATE AGENT MODAL HANDLERS
  // ============================================================================
  
  // Open the Create Agent modal
  const handleOpenCreateAgentModal = () => setShowCreateAgentModal(true);
  
  // Close the Create Agent modal
  const handleCloseCreateAgentModal = () => setShowCreateAgentModal(false);
  
  // Handle successful agent creation
  const handleAgentCreated = (result) => {
    console.log('✅ Agent created successfully:', result);
    console.log('📝 Database record:', result.databaseRecord);
    
    // Show funny popup
    setShowSuccessPopup(true);
    
    // Auto-refresh the agents list after a short delay
    setTimeout(() => {
      if (isUserLoggedIn && userEmail) {
        fetch(`${API_BASE_URL}/api/user-agents?user_email=${encodeURIComponent(userEmail)}`)
          .then(res => {
            if (!res.ok) {
              throw new Error(`Failed to refresh agents: ${res.status}`);
            }
            return res.json();
          })
          .then(data => {
            if (Array.isArray(data)) {
              setAgents(data);
              
              // Update paid categories
              if (addPaidCategory) {
                data.forEach(agent => {
                  addPaidCategory(agent.agent_name);
                });
              }
            }
          })
          .catch(err => {
            console.error('❌ Error refreshing agents list:', err);
          });
      }
    }, 1500); // Wait 1.5 seconds before refreshing
    
    // Auto-close popup after 3 seconds
    setTimeout(() => {
      setShowSuccessPopup(false);
    }, 3000);
  };

  // ============================================================================
  // DELETE AGENT HANDLER
  // ============================================================================
  
  const handleDeleteAgent = async (agent) => {
    // ✅ IMPROVEMENT: Better user_id resolution logic
    // Try to get user_id from multiple sources in order of preference
    let resolvedUserId = agent.user_id || agent.id || agent.userId || userId || null;
    
    console.log('🗑️ Starting delete process for agent:', agent.agent_name);
    console.log('📋 Agent data:', agent);
    console.log('👤 Initial userId:', resolvedUserId);
    
    // If user_id is still not found, try to fetch it from the backend using email
    if (!resolvedUserId && userEmail) {
      try {
        console.log('🔍 Fetching user_id from backend using email...');
        const userRes = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
        
        if (userRes.ok) {
          const userData = await userRes.json();
          console.log('📥 User data response:', userData);
          
          // Try different possible field names for user ID
          resolvedUserId = userData.id || userData.user_id || userData.userId || null;
          
          if (resolvedUserId) {
            console.log('✅ Successfully fetched user_id:', resolvedUserId);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching user_id:', error);
      }
    }

    // Fetch created_at information from purchased_chatbot table
    let agentCreatedAt = 'Unknown';
    try {
      console.log('📅 Fetching agent creation date...');
      const agentRes = await fetch(
        `${API_BASE_URL}/api/agents/${agent.agent_id}/created-at?user_id=${resolvedUserId || ''}`
      );
      
      if (agentRes.ok) {
        const agentData = await agentRes.json();
        console.log('📥 Agent data response for created_at:', agentData);
        
        if (agentData.success && agentData.created_at) {
          agentCreatedAt = new Date(agentData.created_at).toLocaleString();
          console.log('✅ Agent created_at:', agentCreatedAt);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching agent created_at:', error);
    }

    // Fetch phone numbers associated with this agent (both inbound and outbound)
    let phoneNumbers = [];
    try {
      console.log('📞 Fetching phone numbers associated with agent...');
      
      // Use the agent_id parameter which searches both inbound and outbound agent fields
      const phoneRes = await fetch(
        `${API_BASE_URL}/api/phone-numbers?agent_id=${agent.agent_id}&user_id=${resolvedUserId || ''}`
      );
      
      if (phoneRes.ok) {
        const phoneData = await phoneRes.json();
        console.log('📥 Phone data response:', phoneData);
        
        // Check different possible response structures and normalize them
        if (Array.isArray(phoneData)) {
          phoneNumbers = phoneNumbers.concat(phoneData);
        } else if (phoneData.success && phoneData.phoneNumbers) {
          phoneNumbers = phoneNumbers.concat(phoneData.phoneNumbers);
        } else if (phoneData.phoneNumber) {
          phoneNumbers.push(phoneData.phoneNumber);
        } else if (phoneData.phone_numbers) {
          phoneNumbers.push(phoneData.phone_numbers);
        }
      }
      
      // If no phone numbers found and user_id wasn't available, try without user_id filter
      if (phoneNumbers.length === 0 && !resolvedUserId) {
        console.log('🔄 No phone numbers found with user_id, trying without user_id filter...');
        try {
          const phoneResNoUser = await fetch(
            `${API_BASE_URL}/api/phone-numbers?agent_id=${agent.agent_id}`
          );
          
          if (phoneResNoUser.ok) {
            const phoneDataNoUser = await phoneResNoUser.json();
            console.log('📥 Phone data response (no user filter):', phoneDataNoUser);
            
            if (Array.isArray(phoneDataNoUser)) {
              phoneNumbers = phoneNumbers.concat(phoneDataNoUser);
            } else if (phoneDataNoUser.success && phoneDataNoUser.phoneNumbers) {
              phoneNumbers = phoneNumbers.concat(phoneDataNoUser.phoneNumbers);
            } else if (phoneDataNoUser.phoneNumber) {
              phoneNumbers.push(phoneDataNoUser.phoneNumber);
            } else if (phoneDataNoUser.phone_numbers) {
              phoneNumbers.push(phoneDataNoUser.phone_numbers);
            }
          }
        } catch (noUserError) {
          console.log('❌ Phone query without user_id also failed:', noUserError);
        }
      }
      
      // If still no phone numbers found, try agent_phone_connection table
      if (phoneNumbers.length === 0) {
        console.log('🔄 No phone numbers found in phone_numbers table, trying agent_phone_connection table...');
        try {
          const connectionRes = await fetch(
            `${API_BASE_URL}/api/agent-phone-connections?agent_id=${agent.agent_id}`
          );
          
          if (connectionRes.ok) {
            const connectionData = await connectionRes.json();
            console.log('📥 Agent phone connection data response:', connectionData);
            
            if (Array.isArray(connectionData)) {
              connectionData.forEach(conn => {
                if (conn.phone_number) {
                  phoneNumbers.push({ phone_numbers: conn.phone_number });
                }
              });
            } else if (connectionData.success && connectionData.connections) {
              connectionData.connections.forEach(conn => {
                if (conn.phone_number) {
                  phoneNumbers.push({ phone_numbers: conn.phone_number });
                }
              });
            }
          }
        } catch (connectionError) {
          console.log('❌ Agent phone connection query failed:', connectionError);
        }
      }
      
      console.log('📋 Final phone numbers array:', phoneNumbers);
    } catch (error) {
      console.error('❌ Error fetching phone numbers:', error);
    }

    // Show detailed confirmation dialog before deletion
    const confirmMessage = `Are you sure you want to delete agent "${agent.agent_name}"?\n\n` +
      `Agent Details:\n` +
      `• Agent ID: ${agent.agent_id}\n` +
      `• User ID: ${resolvedUserId || 'Unknown'}\n` +
      `• Created At: ${agentCreatedAt}\n` +
      `• Phone Numbers: ${phoneNumbers.length > 0 ? phoneNumbers.map(p => p.phone_numbers || p.phone_number || p).join(', ') : 'None'}\n\n` +
      `This will delete:\n` +
      `• All associated phone numbers from database\n` +
      `• All call logs and connections\n` +
      `• All knowledge base attachments\n\n` +
      `This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      console.log('❌ User cancelled deletion');
      return;
    }
    
    // Start deletion process
    setDeletingAgentId(agent.agent_id);
    try {
      console.log('🗑️ Sending delete request to backend...');
      
      const res = await fetch(
        `${API_BASE_URL}/api/agents/${agent.agent_id}?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: 'DELETE',
        }
      );
      
      // ✅ IMPROVEMENT: Check response status before parsing
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Delete failed with status ${res.status}: ${errorText}`);
      }
      
      const data = await res.json();
      
      if (data.success) {
        console.log('✅ Agent deleted successfully:', data);
        
        // Show detailed success message with deletion summary
        const successMessage = `✅ Agent "${agent.agent_name}" deleted successfully!\n\n` +
          `Deletion Summary:\n` +
          `• Phone Numbers stored in deleted_phone_numbers: ${data.phone_numbers_stored_in_deleted_table || 0}\n` +
          `• Phone Numbers from Database: ${data.phone_numbers_deleted_from_database || 0}\n` +
          `• Phone Connections: ${data.phone_connections_deleted || 0}\n` +
          `• Knowledge Base Attachments: ${data.knowledge_base_attachments_deleted || 0}\n` +
          `• Call Logs: ${data.call_logs_deleted || 0}\n` +
          `• AI Assistant Call Logs: ${data.ai_assistant_call_logs_deleted || 0}\n\n` +
          `The agent and all associated data have been completely removed from the system. ` +
          `Deleted phone numbers have been archived in the deleted_phone_numbers table for record keeping.`;
        
        alert(successMessage);
        
        // Remove the deleted agent from the local state
        setAgents(prev => prev.filter(a => a.agent_id !== agent.agent_id));
      } else {
        console.error('❌ Failed to delete agent:', data);
        alert(`❌ Failed to delete agent: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Network error deleting agent:', err);
      alert(`❌ Network error: ${err.message}`);
    } finally {
      // Reset deleting state
      setDeletingAgentId(null);
    }
  };

  // ============================================================================
  // FILTERING & RENDERING LOGIC
  // ============================================================================
  
  // Filter agents based on search query
  // Searches in agent name and voice ID
  const filteredAgents = agents.filter(agent =>
    agent.agent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.voice_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // If an agent is selected for details, render only the AgentDetails page
  // This provides a full-screen view of a single agent
  if (detailsAgent) {
    return (
      <div style={{ width: '100vw', height: '100vh', margin: 0, padding: 0, background: '#f8fafc' }}>
        <AgentDetails name={detailsAgent.agent_name || 'Unnamed Agent'} />
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <div 
      className="container d-flex flex-column h-100" 
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'flex-start', 
        minHeight: 'calc(100vh - 80px)' 
      }}
    >
      {/* ========================================================================
          HEADER SECTION
          Contains: Title, Search Bar, Create Agent Button (Admin Only)
          ======================================================================== */}
      <div 
        style={{ 
          background: '#fff', 
          borderRadius: '50px', 
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)', 
          padding: '10px 20px', 
          minWidth: '900px', 
          maxWidth: '1100px', 
          width: '100%', 
          color: '#111', 
          marginTop: '0', 
          marginBottom: '24px' 
        }}
      >
        {/* Header row with All Agents title, search bar, and Create Agent button (Admin Only) */}
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: '8px', 
            paddingBottom: '16px', 
            borderBottom: '1px solid #e5e7eb', 
            gap: 16 
          }}
        >
          {/* Page Title */}
          <h2 
            style={{ 
              fontWeight: 'bold', 
              fontSize: '1.3rem', 
              color: '#222', 
              margin: 0 
            }}
          >
            All Agents
          </h2>
          
          {/* Search Form */}
          <form 
            onSubmit={handleSearch} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: '#f3f4f6', 
              borderRadius: '6px', 
              padding: '2px 8px', 
              maxWidth: 350, 
              flex: 1, 
              margin: '0 32px' 
            }}
          >
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              style={{ 
                border: 'none', 
                background: 'transparent', 
                outline: 'none', 
                padding: '6px 8px', 
                fontSize: '1rem', 
                width: '100%' 
              }}
            />
            <button 
              type="submit" 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#2563eb', 
                fontSize: '1.1rem', 
                cursor: 'pointer' 
              }}
            >
              <i className="fas fa-search"></i>
            </button>
          </form>
          
          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Refresh Button */}
            <button
              onClick={handleRefreshAgents}
              disabled={isRefreshing}
              style={{ 
                background: isRefreshing ? '#9ca3af' : '#3b82f6', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '8px', 
                padding: '10px 18px', 
                fontWeight: 600, 
                fontSize: '1rem', 
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              title="Refresh agents list"
            >
              <i className={`fas ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            
            {/* Create Agent Button - Only visible for Admin users */}
            {isAdmin() && (
              <button
                style={{ 
                  background: '#111827', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  padding: '10px 22px', 
                  fontWeight: 600, 
                  fontSize: '1rem', 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#1f2937';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#111827';
                  e.target.style.transform = 'translateY(0)';
                }}
                onClick={handleOpenCreateAgentModal}
              >
                Create an Agent
              </button>
            )}
          </div>
        </div>
        
        {/* ====================================================================
            AGENTS TABLE SECTION
            Shows loading state, error state, empty state, or agents list
            ==================================================================== */}
        <div style={{ width: '100%' }}>
          {/* Loading State */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ color: '#64748b', fontSize: '1rem' }}>
                Loading your agents...
              </div>
            </div>
          ) : /* Error State */ error ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ color: '#ef4444', fontSize: '1rem' }}>
                Error: {error}
              </div>
            </div>
          ) : /* Empty State */ filteredAgents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ color: '#64748b', fontSize: '1rem' }}>
                {isUserLoggedIn 
                  ? 'No agents found. Purchase some agents to get started!' 
                  : 'Please log in to view your agents.'}
              </div>
            </div>
          ) : /* Agents Table */ (
            <div 
              style={{ 
                maxHeight: 500, 
                overflowY: 'scroll', 
                scrollbarWidth: 'thin', 
                scrollbarColor: '#cbd5e1 #f3f4f6' 
              }}
            >
              <table 
                style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse', 
                  marginTop: 8, 
                  fontSize: '0.97rem' 
                }}
              >
                {/* Table Header */}
                <thead 
                  style={{ 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 1, 
                    background: '#f3f4f6' 
                  }}
                >
                  <tr 
                    style={{ 
                      background: '#f3f4f6', 
                      color: '#374151', 
                      fontWeight: 600, 
                      fontSize: '0.97rem' 
                    }}
                  >
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Agent Name
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Language
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Voice
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Model
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Purchase Date
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                
                {/* Table Body - Agent Rows */}
                <tbody>
                  {filteredAgents.map((agent, idx) => (
                    <tr
                      key={agent.agent_id || idx}
                      onClick={() => {
                        // Navigate to agent details page
                        window.history.pushState({}, '', `/agent/${agent.agent_id}`);
                        window.dispatchEvent(new Event('popstate'));
                      }}
                      style={{ 
                        borderBottom: '1px solid #e5e7eb', 
                        background: '#fff', 
                        transition: 'background 0.15s', 
                        fontSize: '0.97rem', 
                        cursor: 'pointer' 
                      }}
                    >
                      {/* Agent Name Column with Icon */}
                      <td 
                        style={{ 
                          padding: '10px 12px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 10, 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        <span 
                          style={{ 
                            fontSize: '1.2rem', 
                            background: '#e0e7ff', 
                            borderRadius: '6px', 
                            padding: 5, 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}
                        >
                          <i 
                            className="fas fa-robot" 
                            style={{ color: '#6366f1', fontSize: '1.1rem' }}
                          ></i>
                        </span>
                        <span 
                          style={{ 
                            fontWeight: 500, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            maxWidth: 180 
                          }}
                        >
                          {agent.agent_name || 'Unnamed Agent'}
                        </span>
                      </td>
                      
                      {/* Language Column */}
                      <td 
                        style={{ 
                          padding: '10px 12px', 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        <span 
                          style={{ 
                            background: '#f3f4f6', 
                            color: '#374151', 
                            borderRadius: '6px', 
                            padding: '3px 10px', 
                            fontWeight: 600, 
                            fontSize: '0.95rem', 
                            display: 'inline-block', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis' 
                          }}
                        >
                          {agent.language || 'en-US'}
                        </span>
                      </td>
                      
                      {/* Voice Column */}
                      <td 
                        style={{ 
                          padding: '10px 12px', 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        <span 
                          style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap', 
                            maxWidth: 90 
                          }}
                        >
                          {agent.voice_id || 'Default Voice'}
                        </span>
                      </td>
                      
                      {/* Model Column */}
                      <td 
                        style={{ 
                          padding: '10px 12px', 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        <span style={{ color: '#64748b', fontSize: '0.95rem' }}>
                          {agent.voice_model || 'Default Model'}
                        </span>
                      </td>
                      
                      {/* Purchase Date Column */}
                      <td 
                        style={{ 
                          padding: '10px 12px', 
                          color: '#64748b', 
                          fontSize: '0.95rem', 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        {agent.purchase_info?.current_date_time ? 
                          new Date(agent.purchase_info.current_date_time).toLocaleDateString() : 
                          'N/A'
                        }
                      </td>
                      
                      {/* Actions Column - Delete Button */}
                      <td>
                        <button 
                          onClick={e => { 
                            e.stopPropagation(); // Prevent row click from firing
                            handleDeleteAgent(agent); 
                          }} 
                          disabled={deletingAgentId === agent.agent_id} 
                          style={{ 
                            background: '#ef4444', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 6, 
                            padding: '6px 14px', 
                            fontWeight: 600, 
                            cursor: deletingAgentId === agent.agent_id ? 'not-allowed' : 'pointer',
                            opacity: deletingAgentId === agent.agent_id ? 0.6 : 1
                          }}
                        >
                          {deletingAgentId === agent.agent_id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* ========================================================================
          MODALS
          ======================================================================== */}
      
      {/* Web Call Modal - For creating web calls with agents */}
      <WebCallModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        agent={selectedAgent}
        userEmail={userEmail}
      />
      
      {/* Create Agent Modal - Full screen modal for creating new agents - Only for Admin */}
      {isAdmin() && showCreateAgentModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              width: '95vw',
              maxWidth: '1200px',
              height: '95vh',
              maxHeight: '800px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseCreateAgentModal}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 22,
                color: '#374151',
                cursor: 'pointer',
                zIndex: 10
              }}
              aria-label="Close"
            >
              ×
            </button>
            
            {/* Create Agent Form Container */}
            <div 
              style={{
                flex: 1,
                overflow: 'auto'
              }}
            >
              {/* ✅ FIX: Pass userId from localStorage instead of state */}
              <CreateAgentForm 
                onClose={handleCloseCreateAgentModal}
                onSuccess={handleAgentCreated}
                userEmail={userEmail}
                userId={userId}
                onOpenAssistant={onOpenAssistant}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* ========================================================================
          SUCCESS POPUP - Funny message when agent is created
          ======================================================================== */}
      {showSuccessPopup && (
        <div 
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '30px 40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            zIndex: 3000,
            maxWidth: '500px',
            textAlign: 'center',
            animation: 'fadeInScale 0.3s ease-out'
          }}
        >
          <style>{`
            @keyframes fadeInScale {
              0% {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.8);
              }
              100% {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
            }
          `}</style>
          
          <div style={{ fontSize: '3rem', marginBottom: '15px' }}>
            🎉
          </div>
          
          <h3 style={{ 
            color: '#fff', 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            margin: '0 0 10px 0' 
          }}>
            Agent Created Successfully!
          </h3>
          
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.95)', 
            fontSize: '1.1rem', 
            margin: '0 0 20px 0',
            lineHeight: '1.5'
          }}>
            {(() => {
              const funnyMessages = [
                "Your new AI agent is getting ready to take over the world... one conversation at a time! 🤖",
                "Brace yourself! Your agent is now learning to be the best assistant ever! 🚀",
                "Your agent just graduated from AI school with honors! Time to celebrate! 🎓",
                "New agent alert! It's already practicing its best 'Hello, how can I help you?' 🎤",
                "Your agent is now live and ready to charm your customers! Let the magic begin! ✨",
                "Congratulations! Your agent just got its first virtual coffee and is ready to work! ☕",
                "Your agent is now in the system, doing its happy dance! 💃",
                "Agent created! It's already planning world domination... of customer service! 🌍"
              ];
              return funnyMessages[Math.floor(Math.random() * funnyMessages.length)];
            })()}
          </p>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '0.9rem'
          }}>
            <i className="fas fa-spinner fa-spin"></i>
            <span>Refreshing your agents list...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;